import { getNativePlatform, isNativeApp } from './platform'
import { checkNativeMicrophonePermission } from '../services/voice-call/native-audio-route'
import { recordCallFlow } from '../debug/call-flow-log'
import { permissionDeniedMessage } from '../services/permissions/permission-denied'

export type MicrophonePermissionState = 'granted' | 'denied' | 'unsupported'
export type NotificationPermissionState = 'granted' | 'denied' | 'default' | 'unsupported'

export type CameraPermissionState = 'granted' | 'denied' | 'unsupported'

export type CallPermissionsSnapshot = {
  microphone: MicrophonePermissionState
  camera?: CameraPermissionState
  notifications: NotificationPermissionState
}

let sessionRequested = false
let lastSnapshot: CallPermissionsSnapshot | null = null

function stopStreamTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    try {
      track.stop()
    } catch {
      /* already stopped */
    }
  }
}

async function requestNativeAvPermission(options: {
  audio: boolean
  video: boolean
}): Promise<{ microphone?: MicrophonePermissionState; camera?: CameraPermissionState }> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return {
      microphone: options.audio ? 'unsupported' : undefined,
      camera: options.video ? 'unsupported' : undefined,
    }
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: options.audio,
      video: options.video,
    })
    stopStreamTracks(stream)
    return {
      microphone: options.audio ? 'granted' : undefined,
      camera: options.video ? 'granted' : undefined,
    }
  } catch {
    if (!options.video) {
      return { microphone: 'denied' }
    }
    if (!options.audio) {
      return { camera: 'denied' }
    }
    // Video call needs both — probe individually so error messages stay specific.
    const microphone = await requestNativeMicrophonePermission()
    const camera = await requestCameraPermission()
    return { microphone, camera }
  }
}

async function probeMicrophoneWithGetUserMedia(reason: string): Promise<MicrophonePermissionState> {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'unsupported'
  }
  const platform = getNativePlatform()
  recordCallFlow('perm:micProbe:start', { reason, platform })
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    stopStreamTracks(stream)
    recordCallFlow('perm:micProbe:end', { reason, platform, result: 'granted' })
    return 'granted'
  } catch {
    recordCallFlow('perm:micProbe:end', { reason, platform, result: 'denied' })
    return 'denied'
  }
}

/**
 * Android: the WebView getUserMedia probe opened and closed a capture stream
 * before every dial, which flipped the audio mode and grabbed / released the
 * car's Bluetooth SCO link (2026-10-20, Denza B5). Read the runtime
 * permission natively; only a mic that is not granted yet falls back to the
 * prompting probe.
 */
async function requestAndroidMicrophonePermission(): Promise<MicrophonePermissionState> {
  if ((await checkNativeMicrophonePermission()) === 'granted') return 'granted'
  return probeMicrophoneWithGetUserMedia('android-not-granted')
}

async function requestNativeMicrophonePermission(): Promise<MicrophonePermissionState> {
  if (getNativePlatform() === 'android') return requestAndroidMicrophonePermission()
  return probeMicrophoneWithGetUserMedia('native')
}

export async function requestCameraPermission(): Promise<CameraPermissionState> {
  if (isNativeApp()) {
    const result = await requestNativeAvPermission({ audio: false, video: true })
    return result.camera ?? 'unsupported'
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'unsupported'
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: false, video: true })
    stopStreamTracks(stream)
    return 'granted'
  } catch {
    return 'denied'
  }
}

export async function requestMicrophonePermission(): Promise<MicrophonePermissionState> {
  if (isNativeApp()) {
    return requestNativeMicrophonePermission()
  }
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return 'unsupported'
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
    stopStreamTracks(stream)
    return 'granted'
  } catch {
    return 'denied'
  }
}

async function requestNotificationPermission(): Promise<NotificationPermissionState> {
  if (isNativeApp()) {
    // Native push uses Capacitor PushNotifications — skip web Notification API.
    return 'unsupported'
  }
  if (typeof Notification === 'undefined') return 'unsupported'
  if (Notification.permission === 'granted') return 'granted'
  if (Notification.permission === 'denied') return 'denied'
  try {
    const result = await Notification.requestPermission()
    return result === 'default' ? 'default' : result
  } catch {
    return 'denied'
  }
}

export function getCallPermissionsSnapshot(): CallPermissionsSnapshot | null {
  return lastSnapshot
}

/** Request mic + notification permissions once per browser session. */
export async function ensureCallPermissions(): Promise<CallPermissionsSnapshot> {
  if (sessionRequested && lastSnapshot) return lastSnapshot

  sessionRequested = true
  const microphone = await requestMicrophonePermission()
  const notifications = isNativeApp() ? 'unsupported' : await requestNotificationPermission()
  lastSnapshot = { microphone, notifications }
  return lastSnapshot
}

/** Always re-check microphone before placing or answering a call (Android can grant after first denial). */
export async function ensureCallPermissionsForCall(
  options?: { video?: boolean },
): Promise<CallPermissionsSnapshot> {
  const wantsVideo = Boolean(options?.video)
  if (isNativeApp() && wantsVideo) {
    // Android: mic read natively, camera probed video-only (no audio capture).
    const av =
      getNativePlatform() === 'android'
        ? {
            microphone: await requestAndroidMicrophonePermission(),
            camera: await requestCameraPermission(),
          }
        : await requestNativeAvPermission({ audio: true, video: true })
    const microphone = av.microphone ?? 'unsupported'
    const camera = av.camera
    const notifications =
      lastSnapshot?.notifications ??
      (isNativeApp() ? 'unsupported' : await requestNotificationPermission())
    lastSnapshot = { microphone, camera, notifications }
    sessionRequested = true
    return lastSnapshot
  }

  const microphone = await requestMicrophonePermission()
  const camera = wantsVideo ? await requestCameraPermission() : undefined
  const notifications =
    lastSnapshot?.notifications ??
    (isNativeApp() ? 'unsupported' : await requestNotificationPermission())
  lastSnapshot = { microphone, camera, notifications }
  sessionRequested = true
  return lastSnapshot
}

export function hasRequestedCallPermissionsThisSession(): boolean {
  return sessionRequested
}

export function microphoneDeniedMessage(): string {
  return permissionDeniedMessage('microphone')
}

export function cameraDeniedMessage(): string {
  return permissionDeniedMessage('camera')
}

export function isCallPermissionSettingsMessage(message: string | undefined): boolean {
  return /settings/i.test(message ?? '')
}
